import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** Pending optimistic bumps from cart:add:initiate (quantities). Drained when cart:update with comingFromProductForm arrives. */
  #pendingOptimisticBumps = [];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cart:add:initiate', this.onOptimisticAdd);
    document.addEventListener('cart:add:revert', this.onOptimisticRevert);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('cart:add:initiate', this.onOptimisticAdd);
    document.removeEventListener('cart:add:revert', this.onOptimisticRevert);
  }

  /**
   * Optimistically bump the cart bubble before the /cart/add response arrives.
   * @param {CustomEvent} event
   */
  onOptimisticAdd = (event) => {
    const qty = Number(event.detail?.quantity) || 1;
    this.#pendingOptimisticBumps.push(qty);
    this.renderCartBubble(qty, true);
  };

  /**
   * Revert an optimistic bump when the add request fails.
   * @param {CustomEvent} event
   */
  onOptimisticRevert = (event) => {
    const qty = Number(event.detail?.quantity) || 1;
    const idx = this.#pendingOptimisticBumps.lastIndexOf(qty);
    if (idx !== -1) this.#pendingOptimisticBumps.splice(idx, 1);
    this.currentCartCount = Math.max(0, this.currentCartCount - qty);
    this.refs.cartBubbleCount.classList.toggle('hidden', this.currentCartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', this.currentCartCount === 0);
    this.classList.toggle('header-actions__cart-icon--has-cart', this.currentCartCount > 0);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    this.scheduleAuthoritativeResync();

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const didError = event.detail.data?.didError === true;

    // The error path already dispatched cart:add:revert; do not re-bump or re-render here.
    if (didError) return;

    // If we already bumped optimistically for this product-form add, drain one pending and exit silently.
    // The optimistic animation already played; replaying it on response would cause a visible "nadskok".
    if (comingFromProductForm && this.#pendingOptimisticBumps.length > 0) {
      this.#pendingOptimisticBumps.shift();
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
      );
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   *
   * 🔴 ⚠️ Hodnota ze `sessionStorage` smí přebít serverem vykreslený počet JEN
   * tehdy, když vznikla AŽ PO startu téhle stránky. Původně stačilo, že je
   * mladší než 10 s — jenže po refreshi je serverové číslo pravda a uložená
   * hodnota z předchozí stránky ne, takže badge ukázal správně (např. 4)
   * a po chvilce to přepsal na starou hodnotu (1). Přesně tohle uživatel
   * nahlásil 2026-08-29 při rychlém přidávání a odebírání.
   *
   * `performance.timeOrigin` = okamžik startu navigace. Cokoli zapsaného dřív
   * je starší než serverový render a musí se ignorovat.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (timestamp > performance.timeOrigin && Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Po každé změně košíku se badge dorovná podle SERVERU.
   *
   * ⚠️ Při rychlém přidávání a odebírání dorazí odpovědi mimo pořadí a badge
   * zůstane na čísle ze staršího requestu (uživatel: „ukazuje 1, i když tam
   * jsou 4"). Optimistické bumpy to zhoršují. Tenhle doraz je odložený, takže
   * proběhne až se série uklidní, a je samoopravný — ať se cestou pokazí
   * cokoli, badge do vteřiny sedí.
   */
  #resyncTimer = 0;

  scheduleAuthoritativeResync = () => {
    window.clearTimeout(this.#resyncTimer);
    this.#resyncTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        const truth = Number(cart?.item_count);
        if (!Number.isFinite(truth) || truth === this.currentCartCount) return;
        this.#pendingOptimisticBumps.length = 0;
        this.renderCartBubble(truth, false, false);
      } catch (_) {
        // Offline nebo zablokovaný request — badge zůstane, jak byl.
      }
    }, 700);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
